import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Map,
  TrendingUp,
  Layers,
  Users,
  MapPin,
  Database,
  Mail,
  Phone,
  ShieldCheck,
} from 'lucide-react';
import Navbar from '@/components/portal/Navbar';
import Footer from '@/components/portal/Footer';
import {
  contentService,
  type HomepageBanner,
  type PublicProject,
  type ServiceItem,
  type FeatureItem,
  type PricingPlan,
  type PublicNotice,
  type PublicVideo,
  type Publication,
  type OfficeContact,
  type TeamProfile,
  type ExecutiveMessage,
  type GisShowcaseItem,
  type EnvironmentalNotice,
} from '@/services/contentService';
import {
  AnnouncementsSection,
  ServicesSection,
  FeaturesSection,
  PricingSection,
  VideosSection,
  PublicationsSection,
  GisShowcaseSection,
  OfficesSection,
  TeamSection,
  ExecutiveMessagesSection,
  EnvironmentalSection,
  PreviewBanner,
} from '@/components/portal/PublicSections';
import {
  AboutPlatformSection,
  CoreServiceAreasSection,
  StrategicProjectsSection,
  MineralIndexSection,
  InvestmentOpportunitiesSection,
  GisPortalSection,
  PlatformServicesSection,
  InformationServicesSection,
  InvestorPortalSection,
} from '@/components/portal/InstitutionalSections';
import { GIISDEC_CONTACT as CONTACT } from '@/lib/contact';

const asArray = <T,>(value: unknown): T[] => {
  if (Array.isArray(value)) {
    return value as T[];
  }

  if (
    value &&
    typeof value === 'object' &&
    'items' in value &&
    Array.isArray((value as { items?: unknown }).items)
  ) {
    return (value as { items: T[] }).items;
  }

  if (
    value &&
    typeof value === 'object' &&
    'data' in value &&
    Array.isArray((value as { data?: unknown }).data)
  ) {
    return (value as { data: T[] }).data;
  }

  return [];
};

// Hero statistics — Option B (governed). Unverified data figures render an
// honest "Awaiting Validation" status (not fabricated numbers). P9B: the third
// stat drops the round-the-clock availability claim (no uptime is sourced) and
// states the access model instead — a capability that is true by construction.
const heroStats: { label: string; value: string; validation: boolean }[] = [
  { label: 'Mapped Concessions', value: 'Awaiting Validation', validation: true },
  { label: 'Critical Minerals', value: 'Awaiting Validation', validation: true },
  { label: 'Secure Access', value: 'Role-Based', validation: false },
];

// Overlapping summary cards — data availability shown via governed validation chips.
const highlightCards = [
  { icon: Layers, title: 'Mapped Concessions', desc: 'Interactive GIS layers of licensed mining concessions.' },
  { icon: Users, title: 'Active Investors', desc: 'Verified investors onboarded through secure KYC.' },
  { icon: Database, title: 'Geological Datasets', desc: 'Approved survey reports and spatial datasets.' },
];

// INFORM / ANALYZE / SUSTAIN pillars (routes preserved).
const pillars = [
  { icon: Database, tag: 'INFORM', title: 'Geological Information', desc: 'Approved geological survey data, borehole logs and the national mineral index, published under data governance.', cta: 'Explore', to: '/services' },
  { icon: Map, tag: 'ANALYZE', title: 'GIS & Spatial Intelligence', desc: 'Interactive concession maps, terrain models and satellite-backed analytics for informed decisions.', cta: 'Open Public GIS', to: '/gis' },
  { icon: TrendingUp, tag: 'SUSTAIN', title: 'Investor Services', desc: 'Onboard, submit KYC, and access verified opportunities across Ghana’s mining sector.', cta: 'Get Access', to: '/investor-login' },
];

// How It Works — four governed steps.
const steps = [
  { n: '01', title: 'Register', desc: 'Create a secure account and choose your role: investor, agency or partner.' },
  { n: '02', title: 'Verify', desc: 'Submit KYC and documents for role-based verification and compliance review.' },
  { n: '03', title: 'Explore', desc: 'Access GIS layers, mineral intelligence and validated geological datasets.' },
  { n: '04', title: 'Engage & Track', desc: 'Access approved opportunities and track your requests from your workspace.' },
];

const AppLayout: React.FC = () => {
  const preview = contentService.isPreviewMode();

  const [banner, setBanner] = useState<HomepageBanner | null>(null);
  const [projects, setProjects] = useState<PublicProject[]>([]);
  const [services, setServices] = useState<ServiceItem[]>([]);
  const [features, setFeatures] = useState<FeatureItem[]>([]);
  const [pricing, setPricing] = useState<PricingPlan[]>([]);
  const [notices, setNotices] = useState<PublicNotice[]>([]);
  const [videos, setVideos] = useState<PublicVideo[]>([]);
  const [publications, setPublications] = useState<Publication[]>([]);
  const [showcase, setShowcase] = useState<GisShowcaseItem[]>([]);
  const [offices, setOffices] = useState<OfficeContact[]>([]);
  const [team, setTeam] = useState<TeamProfile[]>([]);
  const [execMsgs, setExecMsgs] = useState<ExecutiveMessage[]>([]);
  const [envNotices, setEnvNotices] = useState<EnvironmentalNotice[]>([]);

  useEffect(() => {
    let cancelled = false;
    const opts = { preview };
    Promise.all([
      contentService.listBanners(opts),
      contentService.listProjects(opts),
      contentService.listServices(opts),
      contentService.listFeatures(opts),
      contentService.listPricing(opts),
      contentService.listNotices(opts),
      contentService.listVideos(opts),
      contentService.listPublications(opts),
      contentService.listGisShowcase(opts),
      contentService.listOffices(opts),
      contentService.listTeam(opts),
      contentService.listExecutiveMessages(opts),
      contentService.listEnvironmentalNotices(opts),
    ]).then(([b, p, s, f, pp, n, v, pub, gs, off, t, em, en]) => {
  if (cancelled) return;

  const banners = asArray<HomepageBanner>(b);
  const projectItems = asArray<PublicProject>(p);
  const serviceItems = asArray<ServiceItem>(s);
  const featureItems = asArray<FeatureItem>(f);
  const pricingItems = asArray<PricingPlan>(pp);
  const noticeItems = asArray<PublicNotice>(n);
  const videoItems = asArray<PublicVideo>(v);
  const publicationItems = asArray<Publication>(pub);
  const showcaseItems = asArray<GisShowcaseItem>(gs);
  const officeItems = asArray<OfficeContact>(off);
  const teamItems = asArray<TeamProfile>(t);
  const executiveItems = asArray<ExecutiveMessage>(em);
  const environmentalItems = asArray<EnvironmentalNotice>(en);

  setBanner(banners[0] ?? null);
  setProjects(projectItems.slice(0, 6));
  setServices(serviceItems);
  setFeatures(featureItems);
  setPricing(pricingItems);
  setNotices(noticeItems);
  setVideos(videoItems);
  setPublications(publicationItems);
  setShowcase(showcaseItems);
  setOffices(officeItems);
  setTeam(teamItems);
  setExecMsgs(executiveItems);
  setEnvNotices(environmentalItems);
}).catch((error) => {
  if (cancelled) return;

  console.error('Failed to load portal content:', error);

  setBanner(null);
  setProjects([]);
  setServices([]);
  setFeatures([]);
  setPricing([]);
  setNotices([]);
  setVideos([]);
  setPublications([]);
  setShowcase([]);
  setOffices([]);
  setTeam([]);
  setExecMsgs([]);
  setEnvNotices([]);
});
    return () => {
      cancelled = true;
    };
  }, [preview]);

  return (
    <div className="min-h-screen flex flex-col bg-eds-bg font-eds-body">
      <PreviewBanner enabled={preview} />
      <Navbar />
      <main id="main-content" tabIndex={-1} className="flex-1">

        {/* HERO — full-width photographic, dark-green overlay, centered (locked design) */}
        <section className="relative overflow-hidden">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url(${banner?.imageUrl ?? '/giisdec-exploration.jpg'})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
            aria-hidden="true"
          />
          {/* dark-green overlay keeps headline/subtitle AA-legible over the photo */}
          <div className="absolute inset-0 bg-eds-forest/85" aria-hidden="true" />
          <div className="absolute inset-0 bg-gradient-to-b from-eds-forest/70 via-eds-forest/85 to-eds-forest" aria-hidden="true" />

          <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 pt-20 pb-40 lg:pt-28 lg:pb-52 text-center text-white">
            <div className="inline-flex items-center gap-2 rounded-eds-pill bg-white/10 border border-eds-emerald/40 px-3 py-1 text-xs font-semibold text-eds-mint mb-6">
              <span className="h-1.5 w-1.5 rounded-full bg-eds-mint" aria-hidden="true" /> GIISDEC Geological Information &amp; Investment Platform
            </div>

            {banner?.title ? (
              <h1 className="font-eds-display text-4xl sm:text-5xl font-extrabold tracking-[-0.02em] leading-[1.05] mb-5">
                {banner.title}
              </h1>
            ) : (
              <h1 className="font-eds-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-[-0.02em] leading-[1.05] mb-5">
                Discover, Invest &amp; <span className="text-eds-mint">Explore the Earth</span>
              </h1>
            )}

            <p className="mx-auto max-w-2xl text-lg text-white/80 mb-8">
              {banner?.subtitle ??
                "Ghana's central portal for geological data, mineral resource intelligence and secure investor services."}
            </p>

            <div className="flex flex-wrap items-center justify-center gap-4 mb-14">
              <Link
                to={banner?.ctaHref ?? '/investor-login'}
                className="inline-flex items-center gap-2 rounded-eds-pill bg-eds-primary px-6 py-3 font-semibold text-eds-primary-fg transition hover:bg-eds-emerald-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eds-mint focus-visible:ring-offset-2 focus-visible:ring-offset-eds-forest"
              >
                <TrendingUp className="w-5 h-5" /> {banner?.ctaLabel ?? 'Investor Access'} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/gis"
                className="inline-flex items-center gap-2 rounded-eds-pill border border-white/25 bg-white/10 px-6 py-3 font-semibold text-white backdrop-blur transition hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eds-mint focus-visible:ring-offset-2 focus-visible:ring-offset-eds-forest"
              >
                <Map className="w-5 h-5" /> Open Public GIS
              </Link>
            </div>

            {/* Statistics row — Option B governed treatment, locked composition */}
            <div className="mx-auto grid max-w-2xl grid-cols-1 divide-y divide-white/15 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
              {heroStats.map((s) => (
                <div key={s.label} className="px-4 py-3">
                  {s.validation ? (
                    <span className="inline-flex items-center gap-1.5 font-eds-display text-base font-bold text-eds-mint">
                      <ShieldCheck className="w-4 h-4" aria-hidden="true" /> {s.value}
                    </span>
                  ) : (
                    <span className="font-eds-display text-3xl font-bold text-white">{s.value}</span>
                  )}
                  <div className="mt-1 text-xs uppercase tracking-wider text-white/70">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* OVERLAPPING SUMMARY CARDS */}
        <div className="relative z-10 mx-auto -mt-28 max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {highlightCards.map((c) => (
              <div key={c.title} className="rounded-eds-md border border-eds-border bg-eds-surface p-6 shadow-eds-md">
                <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-eds-md bg-eds-success/12 text-eds-emerald-2">
                  <c.icon className="w-5 h-5" aria-hidden="true" />
                </div>
                <h3 className="font-eds-display font-semibold text-eds-fg">{c.title}</h3>
                <p className="mt-1 text-sm text-eds-muted-fg">{c.desc}</p>
                <span className="mt-3 inline-flex items-center gap-1.5 rounded-eds-pill border border-eds-gold/30 bg-eds-gold/15 px-2.5 py-1 text-xs font-semibold text-eds-earth">
                  <ShieldCheck className="w-3.5 h-3.5" aria-hidden="true" /> Official Data Under Validation
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Announcements (dynamic) */}
        <AnnouncementsSection items={notices} />

        {/* ONE SECURE PLATFORM — INFORM / ANALYZE / SUSTAIN */}
        <section className="py-20 bg-eds-bg">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mb-12 text-center">
              <span className="inline-flex items-center rounded-eds-pill bg-eds-success/12 px-3 py-1 text-xs font-semibold text-eds-emerald-2">Portal at a Glance</span>
              <h2 className="mt-4 font-eds-display text-3xl font-bold text-eds-fg sm:text-4xl">One secure platform for Ghana's mineral economy</h2>
              <p className="mx-auto mt-3 max-w-3xl text-eds-muted-fg">
                Access approved geological information, GIS resources, investor services, project information, and
                regulatory support through a central digital platform.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              {pillars.map((p) => (
                <div key={p.title} className="rounded-eds-md border border-eds-border bg-eds-surface p-6 transition hover:shadow-eds-sm">
                  <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-eds-md bg-eds-success/12 text-eds-emerald-2">
                    <p.icon className="w-5 h-5" aria-hidden="true" />
                  </div>
                  <span className="inline-flex items-center rounded bg-eds-surface-2 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-eds-muted-fg">{p.tag}</span>
                  <h3 className="mt-3 font-eds-display text-lg font-semibold text-eds-fg">{p.title}</h3>
                  <p className="mt-2 text-sm text-eds-muted-fg">{p.desc}</p>
                  <Link to={p.to} className="mt-4 inline-flex items-center gap-1.5 font-semibold text-eds-emerald-2 transition-all hover:gap-2.5">
                    {p.cta} <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="py-20 bg-eds-surface-2/40">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mb-12 text-center">
              <span className="inline-flex items-center rounded-eds-pill bg-eds-success/12 px-3 py-1 text-xs font-semibold text-eds-emerald-2">How It Works</span>
              <h2 className="mt-4 font-eds-display text-3xl font-bold text-eds-fg sm:text-4xl">From registration to insight</h2>
              <p className="mx-auto mt-3 max-w-2xl text-eds-muted-fg">A guided, secure and fully digital journey built on Ghana's national data centre.</p>
            </div>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {steps.map((s) => (
                <div key={s.n} className="rounded-eds-md border border-eds-border bg-eds-surface p-6">
                  <div className="flex h-10 w-10 items-center justify-center rounded-eds-md bg-eds-success/15 font-eds-display font-bold text-eds-emerald-2">{s.n}</div>
                  <h3 className="mt-4 font-eds-display font-semibold text-eds-fg">{s.title}</h3>
                  <p className="mt-1.5 text-sm text-eds-muted-fg">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* About the Platform (validation-safe institutional copy) */}
        <AboutPlatformSection />

        {/* Core Service Areas */}
        <CoreServiceAreasSection />

        {/* Platform Services (institutional, validation-safe) */}
        <PlatformServicesSection />

        {/* Information and Services (institutional, validation-safe) */}
        <InformationServicesSection />

        {/* Dynamic services (admin-published, governance-controlled) */}
        <ServicesSection items={services} />

        {/* Dynamic features (admin-published, governance-controlled) */}
        <FeaturesSection items={features} />

        {/* Executive messages */}
        <ExecutiveMessagesSection items={execMsgs} />

        {/* Investor Portal */}
        <InvestorPortalSection />

        {/* GIS showcase (dynamic) */}
        <GisShowcaseSection items={showcase} />

        {/* Strategic Projects — dynamic when approved, validation-safe otherwise */}
        {projects.length > 0 ? (
          <section className="py-16 bg-eds-surface">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
                <div>
                  <h2 className="text-3xl font-bold text-eds-fg">Strategic Projects</h2>
                  <p className="text-eds-muted-fg mt-2">
                    Approved project information published following departmental validation.
                  </p>
                </div>
                <Link to="/projects" className="text-eds-emerald-2 font-medium inline-flex items-center gap-2 hover:gap-3 transition-all">
                  View all <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {projects.map((p) => (
                  <div key={p.id} className="bg-eds-surface border border-eds-border rounded-lg overflow-hidden hover:shadow-lg transition group">
                    <div className="h-40 bg-gradient-to-br from-eds-forest-2 via-eds-emerald-2 to-eds-gold relative">
                      <div className="absolute top-3 right-3 px-2 py-1 text-xs font-medium bg-eds-surface/90 text-eds-fg rounded">
                        {p.status}
                      </div>
                    </div>
                    <div className="p-5">
                      <h3 className="font-bold text-eds-fg text-lg mb-2 group-hover:text-eds-emerald-2 transition">{p.name}</h3>
                      <div className="flex items-center gap-1 text-sm text-eds-muted-fg mb-1">
                        <MapPin className="w-4 h-4" /> {p.region}
                      </div>
                      <div className="flex items-center gap-1 text-sm text-eds-muted-fg">
                        <Layers className="w-4 h-4" /> {p.type}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        ) : (
          <StrategicProjectsSection />
        )}

        {/* Mineral Index (validation-safe schema preview) */}
        <MineralIndexSection />

        {/* Investment Opportunities (validation-safe) */}
        <InvestmentOpportunitiesSection />

        {/* GIS Portal */}
        <GisPortalSection />

        {/* Dynamic pricing */}
        <PricingSection plans={pricing} />

        {/* Videos */}
        <VideosSection items={videos} />

        {/* Publications */}
        <PublicationsSection items={publications} />

        {/* Environment / public safety */}
        <EnvironmentalSection items={envNotices} />

        {/* Team */}
        <TeamSection items={team} />

        {/* Offices */}
        <OfficesSection items={offices} />

        {/* CTA BAND — locked design */}
        <section className="py-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="rounded-eds-md bg-gradient-to-br from-eds-forest to-eds-forest-2 px-6 py-14 text-center text-white sm:px-10">
              <h2 className="font-eds-display text-3xl font-bold sm:text-4xl">Ready to explore Ghana's mineral wealth?</h2>
              <p className="mx-auto mt-3 max-w-2xl text-white/75">
                Join verified investors and agencies on the official geo-information platform for sustainable development.
              </p>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
                <Link to="/investor-login#register" className="inline-flex items-center gap-2 rounded-eds-pill bg-eds-primary px-6 py-3 font-semibold text-eds-primary-fg transition hover:bg-eds-emerald-2">
                  Get Started <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/about" className="inline-flex items-center gap-2 rounded-eds-pill border border-white/25 bg-white/10 px-6 py-3 font-semibold text-white transition hover:bg-white/20">
                  Learn more
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Contact strip */}
        <section className="py-16 bg-gradient-to-br from-eds-forest to-eds-forest text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h2 className="text-3xl font-bold mb-2">Get in touch</h2>
              <p className="text-white/70">{CONTACT.head.label}: {CONTACT.head.address}</p>
              <Link to="/contact" className="inline-flex items-center gap-2 mt-4 text-eds-mint hover:text-white font-medium">
                Contact us <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="flex items-start gap-3">
              <Mail className="w-5 h-5 text-eds-mint mt-1 flex-shrink-0" />
              <div>
                <div className="text-sm text-white/70 uppercase tracking-wider">Email</div>
                <a href={CONTACT.emailHref} className="font-medium hover:text-eds-mint break-all">
                  {CONTACT.email}
                </a>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Phone className="w-5 h-5 text-eds-mint mt-1 flex-shrink-0" />
              <div>
                <div className="text-sm text-white/70 uppercase tracking-wider">Phone</div>
                <a href={CONTACT.phoneHref} className="font-medium hover:text-eds-mint">
                  {CONTACT.phoneDisplay}
                </a>
              </div>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
};

export default AppLayout;
